<template>
  <div class="integrations-menu-item">
    <span class="integrations-description">Unlock insights and funding</span>

    <router-link
      v-if="config.enableAccountingSoftwareIntegration"
      :class="[
        'integration-source',
        {
          active: isAccountingSoftwareConnected,
          error: apiDeckError,
          pending: isAccountingSoftwarePending
        }
      ]"
      :to="{ name: 'integrations' }"
    >
      <span class="material-symbols-outlined source-icon">insights</span>
      <p>Accounting software</p>
      <span class="material-symbols-outlined source-status">{{ statusIcon(isAccountingSoftwareConnected, isAccountingSoftwarePending, apiDeckError) }}</span>
    </router-link>

    <router-link
      v-if="config.enableOpenBankingIntegration || config.enablePlaidIntegration"
      :class="['integration-source', { active: isBankAccountConnected, pending: isBankAccountPending, error: isBankAccountError }]"
      :to="{ name: 'integrations' }"
    >
      <span class="material-symbols-outlined source-icon">account_balance</span>
      <p>Bank account</p>
      <span class="material-symbols-outlined source-status">{{ statusIcon(isBankAccountConnected, isBankAccountPending, isBankAccountError) }}</span>
    </router-link>
  </div>
</template>

<script>
import { apiDeckIntegrationRoutine, apiDeckGetters } from '@/store/modules/apiDeck/routines'
import { companyGetters } from '@/store/modules/company/routines'
import { bankGetters, bankMandateListRoutine } from '@/store/modules/bank/routines'
import { plaidGetters, plaidItemsRoutine } from '@/store/modules/plaid/routines'
import { mapActions, mapGetters } from 'vuex'
import useWhitelabel from '@/composables/useWhitelabel'

export default {
  name: 'sidebar-integrations',
  setup() {
    const { config } = useWhitelabel('portal')
    return { config }
  },
  methods: {
    ...mapActions({
      fetchApiDeckIntegration: apiDeckIntegrationRoutine.TRIGGER,
      getBankMandateList: bankMandateListRoutine.TRIGGER,
      fetchPlaidItems: plaidItemsRoutine.TRIGGER
    }),
    statusIcon(connected, pending, error) {
      if (error) return 'error'
      if (pending) return 'refresh'
      return connected ? 'check_circle' : 'add_circle'
    }
  },
  async mounted() {
    if (!this.apiDeckIntegration) {
      try {
        await this.fetchApiDeckIntegration(this.companyId)
      } catch (error) {
        console.error('Error fetching ApiDeck integration:', error)
      }
    }
    if (this.config.enableOpenBankingIntegration) {
      await this.getBankMandateList(this.companyId)
    }
    if (this.config.enablePlaidIntegration) {
      await this.fetchPlaidItems(this.companyId).catch(e => console.error('Error fetching Plaid items:', e))
    }
  },
  computed: {
    ...mapGetters({
      companyId: companyGetters.COMPANY_ID,
      bankMandateStatus: bankGetters.BANK_MANDATE_STATUS,
      apiDeckIntegration: apiDeckGetters.APIDECK_INTEGRATION,
      apiDeckError: apiDeckGetters.APIDECK_ERROR,
      plaidStatus: plaidGetters.PLAID_STATUS
    }),
    isAccountingSoftwareConnected() {
      return !this.apiDeckError && this.apiDeckIntegration?.status === 'connected'
    },
    isAccountingSoftwarePending() {
      return this.apiDeckIntegration?.status === 'pending'
    },
    isBankAccountConnected() {
      if (this.config.enablePlaidIntegration) return this.plaidStatus === 'connected'
      return this.bankMandateStatus?.toLowerCase() === 'processingcompleted'
    },
    isBankAccountPending() {
      if (this.config.enablePlaidIntegration) return this.plaidStatus === 'pending'
      return this.bankMandateStatus?.toLowerCase() === 'fetchingdata'
    },
    isBankAccountError() {
      if (this.config.enablePlaidIntegration) return this.plaidStatus === 'failed'
      const errorStatuses = ['alreadyregistered', 'authorsationexpired', 'failed']
      return errorStatuses.includes(this.bankMandateStatus?.toLowerCase())
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/swoop/_variables.scss';
@import '@/assets/styles/swoop/_mixins.scss';

$sidebar-ink: rgba(255, 255, 255, 0.8);
$sidebar-ink-faint: rgba(255, 255, 255, 0.55);
$sidebar-hover: rgba(255, 255, 255, 0.07);
$sidebar-selected-text: var(--color-secondary-200);
$sidebar-selected-fill: color-mix(in srgb, var(--color-secondary-500) 26%, transparent);
$source-line: rgba(255, 255, 255, 0.18);
$source-fill: rgba(255, 255, 255, 0.03);

.integrations-menu-item {
  padding: 6px 12px 8px;
  height: fit-content;
  cursor: default;

  :hover,
  :active,
  :visited,
  :focus {
    text-decoration: none;
  }

  .integrations-description {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.45;
    color: $sidebar-ink-faint;
    display: block;
    margin: 6px 0 10px;
  }

  .integration-source {
    display: flex;
    height: 36px;
    padding: 0 10px;
    align-items: center;
    gap: 9px;
    align-self: stretch;
    border-radius: 8px;
    border: 1px solid $source-line;
    background-color: $source-fill;
    margin-bottom: 6px;
    color: $sidebar-ink;
    transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;

    &:hover {
      border-color: rgba(255, 255, 255, 0.38);
      background-color: $sidebar-hover;
      color: var(--color-white);

      .source-icon,
      .source-status {
        font-variation-settings: 'FILL' 1;
      }
    }

    &:focus-visible {
      outline: 1px solid var(--color-white);
      outline-offset: -1px;
    }

    p {
      margin: 0;
      flex-grow: 1;
      min-width: 0;
      text-align: left;
      color: inherit;
      font-size: 12.5px;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .source-icon,
    .source-status {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      flex-shrink: 0;
      color: inherit;
    }

    .source-status {
      color: $sidebar-ink-faint;
    }

    // Connected is the only state that fills; the rest keep the quiet surface and say it in the glyph alone.
    &.active {
      border-color: color-mix(in srgb, var(--color-secondary-500) 55%, transparent);
      background-color: $sidebar-selected-fill;
      color: var(--color-white);

      .source-icon,
      .source-status {
        color: $sidebar-selected-text;
        font-variation-settings: 'FILL' 1;
      }
    }

    &.active:hover {
      border-color: color-mix(in srgb, var(--color-secondary-200) 85%, transparent);
      background-color: color-mix(in srgb, var(--color-secondary-500) 36%, transparent);
    }

    &.pending .source-status {
      color: $sidebar-selected-text;
    }

    &.error .source-status {
      color: var(--color-error-300);
    }
  }
}
</style>
